import { useEffect, useRef } from 'react';

interface DeckFrameProps {
  /** Full HTML document from the backend, rendered by the deck renderer. */
  html: string;
  /** Slide to show, counted over visible slides only — the deck document hides the rest. */
  slideIndex: number;
  /** Fired when the deck itself moves: arrow keys, space, the nav buttons. */
  onSlideChange?: (index: number) => void;
  /** Fill the whole parent instead of keeping a 16/9 box (full-screen view). */
  fill?: boolean;
  busy?: boolean;
  className?: string;
}

interface DeckMessage {
  source?: string;
  type?: string;
  index?: number;
  total?: number;
}

/**
 * The deck preview.
 *
 * Same isolation as the landing preview — `srcdoc` so no token lands in a browsable
 * address, and a sandbox without `allow-same-origin` so the generated document gets an
 * opaque origin and cannot reach the app around it. Scripts stay on: the deck's own
 * navigation lives inside the document, and it is what reports the current slide back.
 *
 * A deck is 16/9, not a scrolling page, so the frame keeps that ratio rather than
 * filling whatever height is left over. The one exception is the full-screen view,
 * where the browser hands us the whole display and letterboxing is the document's job.
 */
export function DeckFrame({ html, slideIndex, onSlideChange, fill, busy, className }: DeckFrameProps) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  // In a ref so the message listener is attached once, not on every parent render.
  const onChange = useRef(onSlideChange);
  onChange.current = onSlideChange;
  // What the frame last told us. Echoing that index straight back would fight the
  // user's own arrow key, so a matching value is skipped.
  const reported = useRef(-1);

  useEffect(() => {
    function onMessage(event: MessageEvent<DeckMessage>) {
      const data = event.data;
      if (!data || data.source !== 'builder-deck') return;
      if (frameRef.current && event.source !== frameRef.current.contentWindow) return;
      if (data.type !== 'slide' || typeof data.index !== 'number') return;

      reported.current = data.index;
      onChange.current?.(data.index);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (slideIndex === reported.current) return;
    frameRef.current?.contentWindow?.postMessage(
      { source: 'builder-deck-host', type: 'goto', index: slideIndex },
      '*',
    );
  }, [slideIndex, html]);

  // A freshly rendered document starts at slide 1, so the host's idea of the current
  // slide has to be re-sent once it has loaded.
  const handleLoad = () => {
    reported.current = -1;
    if (slideIndex === 0) return;
    frameRef.current?.contentWindow?.postMessage(
      { source: 'builder-deck-host', type: 'goto', index: slideIndex },
      '*',
    );
  };

  return (
    <div
      className={
        'relative w-full overflow-hidden rounded-[16px] bg-white shadow-sm ' +
        (fill ? 'h-full ' : 'aspect-video max-h-full ') +
        (className ?? '')
      }
    >
      <iframe
        ref={frameRef}
        title="deck-preview"
        srcDoc={html}
        onLoad={handleLoad}
        sandbox="allow-scripts"
        className="block h-full w-full border-0"
      />
      {busy && <div className="pointer-events-none absolute inset-0 bg-white/60" aria-hidden="true" />}
    </div>
  );
}
